/**
 * เขียน revision ถัดไปของ BOM หรือ Routing — `POST .../bom` / `POST .../routing`
 *
 * **ฟอร์มนี้ไม่ใช่ฟอร์มแก้ไข** และนั่นเป็นการตัดสินใจของ contract ไม่ใช่ข้อจำกัดของ
 * หน้าจอ: ไม่มี `PATCH` และไม่มี `DELETE` ในกลุ่มนี้เลย การเปลี่ยนสูตรคือการปิดฉบับ
 * เดิมแล้วเขียนฉบับถัดไป เพราะใบสั่งผลิตที่ release แล้ว snapshot `bom_id` ไว้ และ
 * ต้องอ่านสูตรที่ตัวเองถูกสร้างมาจากได้ตลอดอายุของ order (`BUSINESS_RULES.md` ข้อ 2)
 *
 * สิ่งที่จงใจ "ไม่มี" ที่นี่ และเหตุผล:
 *
 * - **ไม่มีช่อง `revision`** และไม่มีทางให้ผู้ใช้แตะมันเลย เซิร์ฟเวอร์เป็นผู้กำหนด
 *   เลขถัดไป — เลขที่ client เลือกเองคือการแข่งกันเขียนทับ
 *   `UNIQUE(product_id, plant_id, revision)` (contract)
 * - **ไม่มีปุ่มลบแถวออกจากฉบับที่ใช้อยู่** เพราะสิ่งที่ส่งคือ**ชุดเต็มของฉบับใหม่**
 *   ไม่ใช่ส่วนต่าง การ "ลบแถว" จึงแปลว่าไม่ใส่แถวนั้นลงในฉบับใหม่ ซึ่งไม่แตะฉบับเดิม
 * - **ไม่มีการตรวจว่าชิ้นส่วนเป็นสินค้าตัวเองที่ฝั่งนี้** — เซิร์ฟเวอร์เป็นผู้ตัดสิน
 *   และตอบกฎ `COMPONENT_CANNOT_BE_THE_PRODUCT_ITSELF` มาให้แสดง การเดาแทนที่นี่จะ
 *   กลายเป็นกติกาชุดที่สองที่เถียงกับเซิร์ฟเวอร์ได้
 *
 * ชุดรายการว่างถูกปฏิเสธ**ก่อนส่ง** ตามที่ contract บอกว่าเซิร์ฟเวอร์จะตอบ `400`:
 * ไม่ใช่เพื่อแทนที่ด่านของเซิร์ฟเวอร์ แต่เพื่อไม่ให้ผู้ใช้ต้องรอ round-trip เพื่อรู้
 * สิ่งที่หน้าจอรู้อยู่แล้ว
 */

import { useRef, useState, type FormEvent } from 'react';

import { BomFailureNotice } from './BomFailureNotice';
import { toEffectiveDate } from './bomView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { createFormula, type FormulaFailure } from '../../lib/bom/client';
import type { FormulaRevision, FormulaSpec, RowValues } from '../../lib/bom/types';

export interface FormulaCreateFormProps<TRow> {
  readonly spec: FormulaSpec<TRow>;
  readonly productId: string;
  readonly plantCode: string;
  /** แถวของฉบับที่ใช้อยู่ — มีไว้ให้ "เริ่มจากฉบับปัจจุบัน" เท่านั้น */
  readonly currentRows: readonly TRow[] | null;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** เซิร์ฟเวอร์บันทึกฉบับใหม่แล้ว — ผู้เรียกเป็นผู้ไปดึงของจริงมาแสดง */
  readonly onCreated: (revision: FormulaRevision<TRow>) => void;
}

/**
 * แถวหนึ่งแถวระหว่างกรอก — มี id ของตัวเองเพราะลำดับที่ผู้ใช้พิมพ์ (`item_no`) ยัง
 * ว่างหรือซ้ำกันได้ระหว่างทาง และตำแหน่งในอาร์เรย์เปลี่ยนทุกครั้งที่ลบแถวกลางชุด
 */
interface EditorRow {
  readonly id: number;
  readonly values: RowValues;
}

type SubmitState =
  | { readonly status: 'idle' }
  | { readonly status: 'submitting' }
  | { readonly status: 'invalid-form'; readonly problems: readonly string[] }
  | FormulaFailure;

export function FormulaCreateForm<TRow>({
  spec,
  productId,
  plantCode,
  currentRows,
  apiBaseUrl,
  fetchImpl,
  onCreated,
}: FormulaCreateFormProps<TRow>) {
  const [effectiveDay, setEffectiveDay] = useState('');
  const [rows, setRows] = useState<readonly EditorRow[]>(() => [
    { id: 0, values: spec.blankRow() },
  ]);
  const nextRowId = useRef(1);
  const [submit, setSubmit] = useState<SubmitState>({ status: 'idle' });

  /** id ของแถวใหม่ — แถวที่ถูกลบไม่คืนเลขเดิมให้ใคร */
  const takeRowId = (): number => {
    const id = nextRowId.current;
    nextRowId.current += 1;
    return id;
  };

  const updateCell = (id: number, key: string, value: string) => {
    setRows((current) =>
      current.map((row) => (row.id === id ? { ...row, values: { ...row.values, [key]: value } } : row)),
    );
  };

  const onFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submit.status === 'submitting') {
      return;
    }

    const problems: string[] = [];

    const effectiveDate = toEffectiveDate(effectiveDay);
    if (effectiveDate === null) {
      problems.push('ต้องระบุวันที่มีผลของฉบับใหม่');
    }

    // ชุดว่าง = สูตรที่ยังไม่ได้เขียน — contract ให้เซิร์ฟเวอร์ตอบ 400 และหน้าจอ
    // ไม่ควรปล่อยให้ผู้ใช้รอคำตอบนั้น
    if (rows.length === 0) {
      problems.push(
        `ต้องมีอย่างน้อยหนึ่ง${spec.rowNoun} — หนึ่งคำขอคือหนึ่งฉบับที่สมบูรณ์ ไม่ใช่ส่วนต่าง`,
      );
    }

    const built: TRow[] = [];
    rows.forEach((row, index) => {
      const result = spec.buildRow(row.values, index + 1);
      if (result.ok) {
        built.push(result.row);
      } else {
        problems.push(...result.problems);
      }
    });

    const seen = new Set<string>();
    for (const row of built) {
      const key = spec.rowKey(row);
      if (seen.has(key)) {
        problems.push(`${spec.rowKeyLabel} ${key} ซ้ำกันภายในคำขอเดียว`);
      }
      seen.add(key);
    }

    if (problems.length > 0 || effectiveDate === null) {
      setSubmit({ status: 'invalid-form', problems });
      return;
    }

    setSubmit({ status: 'submitting' });
    const result = await createFormula(
      spec,
      productId,
      { plant_code: plantCode, effective_date: effectiveDate, rows: built },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status !== 'ready') {
      // ความล้มเหลวไม่ลบสิ่งที่กรอกไว้ — กฎที่ชนส่วนใหญ่แก้ได้ด้วยการแตะค่าเดียว
      setSubmit(result);
      return;
    }

    setSubmit({ status: 'idle' });
    setRows([{ id: takeRowId(), values: spec.blankRow() }]);
    setEffectiveDay('');
    onCreated(result.revision);
  };

  return (
    <section className="cap-card cap-card--form" aria-label={`เขียน${spec.title}ฉบับถัดไป`}>
      <div className="cap-card__head">
        <h3 className="cap-card__title">
          เขียนฉบับถัดไป
          <InfoHint
            label="เกี่ยวกับการเขียนฉบับถัดไป"
            text={
              <>
                สิ่งที่ส่งคือ<strong>ชุดเต็มของฉบับใหม่</strong> ไม่ใช่เฉพาะสิ่งที่เปลี่ยน —
                ฉบับที่ใช้อยู่จะถูกปิดด้วยวันที่มีผลนี้ และเลข revision เป็นของเซิร์ฟเวอร์
                ฟอร์มนี้จึงไม่มีช่องให้ระบุ
              </>
            }
          />
          <InfoHint
            label="เกี่ยวกับเลข revision"
            text={
              <>
                <StatusBadge tone="info">เลข revision เป็นของเซิร์ฟเวอร์</StatusBadge> หน้าจอนี้ไม่ส่ง{' '}
                <code className="cap-table__code">revision</code> ไปกับคำขอ
                และจะแสดงเลขที่เซิร์ฟเวอร์กำหนดให้หลังบันทึกสำเร็จ
              </>
            }
          />
        </h3>
      </div>

      {submit.status === 'invalid-form' ? (
        <div
          className="cap-banner cap-banner--warning"
          role="status"
          data-testid="formula-create-invalid"
        >
          <strong>ยังส่งไม่ได้ — ข้อมูลยังไม่ครบ</strong>
          <span>{submit.problems.join(' · ')}</span>
        </div>
      ) : null}

      {submit.status === 'idle' ||
      submit.status === 'submitting' ||
      submit.status === 'invalid-form' ? null : (
        <BomFailureNotice failure={submit} context="create" plantCode={plantCode} />
      )}

      <form
        className="cap-form"
        data-testid="formula-create-form"
        onSubmit={(event) => void onFormSubmit(event)}
        noValidate
      >
        <label className="cap-field">
          <span className="cap-field__label">
            วันที่มีผล (effective_date)
            <InfoHint
              label="เกี่ยวกับวันที่มีผล"
              text="ต้องอยู่หลังวันที่ฉบับปัจจุบันเริ่มมีผล — ฉบับเดิมถูกปิดด้วยวันนี้พอดี ทั้งหมดอยู่ในธุรกรรมเดียว ไม่มีสถานะครึ่ง ๆ"
            />
          </span>
          <input
            className="cap-input"
            type="date"
            name="effective_date"
            data-testid="formula-effective-date"
            value={effectiveDay}
            onChange={(event) => {
              setEffectiveDay(event.target.value);
            }}
          />
        </label>

        <div className="cap-table-wrap">
          <table className="cap-table">
            <caption className="cap-visually-hidden">
              {spec.rowNoun}ทั้งชุดของฉบับใหม่ — ชุดเต็ม ไม่ใช่ส่วนต่างจากฉบับปัจจุบัน
            </caption>
            <thead>
              <tr>
                {spec.fields.map((field) => (
                  <th scope="col" key={field.key}>
                    {field.label}
                  </th>
                ))}
                <th scope="col">นำออกจากฉบับใหม่</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id} data-testid={`formula-input-row-${index}`}>
                  {spec.fields.map((field) => (
                    <td key={field.key}>
                      <input
                        className="cap-input"
                        name={`${field.key}-${index}`}
                        aria-label={`${field.label} แถวที่ ${index + 1}`}
                        data-testid={`formula-input-${index}-${field.key}`}
                        inputMode={
                          field.kind === 'decimal'
                            ? 'decimal'
                            : field.kind === 'integer'
                              ? 'numeric'
                              : undefined
                        }
                        value={row.values[field.key] ?? ''}
                        onChange={(event) => {
                          updateCell(row.id, field.key, event.target.value);
                        }}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="cap-button cap-button--ghost"
                      data-testid={`formula-row-remove-${index}`}
                      onClick={() => {
                        setRows((current) => current.filter((entry) => entry.id !== row.id));
                      }}
                    >
                      นำออก
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length === 0 ? (
          <p className="cap-card__foot" data-testid="formula-create-no-rows">
            ยังไม่มี{spec.rowNoun}ในฉบับนี้ — ฉบับที่ไม่มี{spec.rowNoun}เลยคือฉบับที่ยังไม่ได้เขียน
            หน้าจอจะไม่ส่งให้เซิร์ฟเวอร์
          </p>
        ) : null}

        <div className="cap-form__actions">
          <button
            type="button"
            className="cap-button cap-button--ghost"
            data-testid="formula-row-add"
            onClick={() => {
              setRows((current) => [...current, { id: takeRowId(), values: spec.blankRow() }]);
            }}
          >
            เพิ่ม{spec.rowNoun}
          </button>
          {currentRows === null || currentRows.length === 0 ? null : (
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="formula-copy-current"
              onClick={() => {
                // คัดลอกมาเป็น**ค่าเริ่มต้นของฉบับใหม่** ไม่ใช่การแก้ฉบับเดิม —
                // สิ่งที่ส่งยังเป็นชุดเต็มที่อยู่ในฟอร์มนี้เสมอ
                setRows(
                  currentRows.map((row) => ({ id: takeRowId(), values: spec.toValues(row) })),
                );
                setSubmit({ status: 'idle' });
              }}
            >
              เริ่มจากฉบับที่ใช้อยู่
            </button>
          )}
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="formula-create-submit"
            disabled={submit.status === 'submitting'}
          >
            {submit.status === 'submitting' ? 'กำลังบันทึก…' : 'บันทึกเป็นฉบับถัดไป'}
          </button>
        </div>
      </form>
    </section>
  );
}
